// client/src/Game.jsx

/**
 * This file displays the main header bar, manages the live countdown timers, 
 * tracks real-time cash bonuses, and renders the current stage layout.
 */

import React from "react";
import {
  usePlayer,
  usePlayers,
  useRound,
  useStage,
  useStageTimer,
  useGame,
} from "@empirica/core/player/classic/react";
import { Stage } from "./Stage";

// ==========================================
// 1. Main Game Layout Component
// ==========================================
export function Game() {
  const player = usePlayer();
  const players = usePlayers();
  const round = useRound();
  const stage = useStage();
  const timer = useStageTimer();
  const game = useGame();

  // Safety loading fallback until network state syncs
  if (!player || !round || !stage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 font-sans">
        <div className="app-status text-gray-500 animate-pulse">
          Synchronizing laboratory networks...
        </div>
      </div>
    );
  }

  const stageName = stage.get("name");
  const activeTask = round.get("task");
  const currentRoundNum = round.get("roundNumber");

  // The single source of truth for the bonus is the server's accumulatedBonus,
  // written by calculatePredictionScores when the feedback stage opens. There is
  // no local recomputation here: this used to add a "live preview" for the round
  // being displayed, because scoring ran at the END of the feedback stage and the
  // server total lagged the screen by one round.
  const displayedBonus = player.get("accumulatedBonus") ?? 0;
  const remainingSeconds = timer?.remaining !== undefined ? Math.round(timer.remaining / 1000) : null;

  // ==========================================
  // 2. Header Label Formatting Helpers
  // ==========================================
  const formatStageHeader = (name) => {
    switch (name) {
      case "initialPrediction":
      case "initial_prediction":
        return "Stage 1: First Prediction";
      case "finalPrediction":
      case "final_prediction":
        return "Stage 2: Second Prediction";
      case "feedback":
        return "Stage 3: Feedback";
      case "chooseAdvisors":
      case "choose_advisors":
        return "Choosing Advisors";
      case "variableQuiz":
        return "One Quick Check";
      default:
        if (!name) return "--";
        const spaced = name.replace(/([A-Z])/g, " $1").trim();
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    }
  };

  // Helper formatting for secondary subtitle track tags
  const getSubheaderText = () => {
    if (activeTask === "variable_introduction" || stageName === "viewVariablesAndSamples") {
      return <span className="text-gray-800 font-semibold">Loan Information Setup</span>;
    }
    if (currentRoundNum === 0) {
      return (
        <>
          Practice Round &bull;{" "}
          <span className="text-gray-800 font-semibold">{formatStageHeader(stageName)}</span>
        </>
      );
    }
    return (
      <>
        Round {currentRoundNum} &bull;{" "}
        <span className="text-gray-800 font-semibold">{formatStageHeader(stageName)}</span>
      </>
    );
  };

  // ==========================================
  // 3. Main Return Component Render
  // ==========================================
  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans antialiased text-gray-900">
      {/* Global Header Header Dashboard Panel */}
      <header className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="app-page-title text-gray-900">
              Loan Risk Assessment Game
            </h1>
            <p className="app-page-subtitle text-gray-500 mt-0.5">
              {getSubheaderText()}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Countdown timer widget */}
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <div className="text-right">
                <div className="app-metric-label text-amber-600">
                  Time Remaining
                </div>
                <div className="app-metric-value font-mono text-amber-900 mt-0.5">
                  {remainingSeconds !== null ? `${remainingSeconds}s` : "--s"}
                </div>
              </div>
            </div>

            {/* HIDE ACCUMULATED CASH BONUS COUNTER DURING THE PRACTICE ROUND */}
            {currentRoundNum > 0 && (
              <div className="bg-gray-50 border border-gray-200/80 px-4 py-1.5 rounded-xl flex items-center gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <div className="text-right">
                  <div className="app-metric-label text-gray-400">
                    Bonus
                  </div>
                  <div className="app-metric-value text-gray-800 mt-0.5 font-mono">
                    ${displayedBonus.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Experiment Body Component Render Target Container Slot */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-2 flex flex-col items-center justify-start">
        <Stage />
      </main>
    </div>
  );
}